import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/utils';
import { formatReportingAmount } from '../../lib/reportingMoney';
import { humanizeIndustry } from '../../lib/accountAnalytics';
import { NO_AM_KEY } from '../../lib/accountAnalyticsSnapshot';
import {
  BUSINESS_LENS_PRESETS,
  businessLensRange,
  DEFAULT_BUSINESS_LENS_PRESET,
  type BusinessLensDatePreset,
} from '../../lib/reportingDateRange';
import {
  earliestCountMonth,
  earliestRevenueMonth,
  pivotCountSeries,
  pivotRevenueSeries,
  seriesCurrencies,
  type BusinessLensMetric,
  type BusinessSeriesBase,
  type BusinessSeriesDimension,
  type CountSeriesDimension,
  type RevenueDimension,
} from '../../lib/businessRevenueSeries';
import type { AccountAnalyticsRow } from '../../types/accountAnalytics';
import { getAccountCountHistory } from './api';
import { getXeroRevenue } from '../xero';
import { ChartStyleToggle } from '../reporting/ChartStyleToggle';
import { DEFAULT_CHART_STYLE, type ChartStyle } from '../reporting/chartPrefs';
import { BusinessTimeSeriesChart } from './BusinessTimeSeriesChart';

// Business lens v2 — the invoiced revenue AND account counts as a HISTORIC monthly
// time-series over a selectable range, sliced by dimension into a coloured
// multi-series with a clickable legend + chart-style chooser. Revenue (Xero) is
// per-currency and joined to kyck-account dimensions; counts (Phase C) read the
// pre-aggregated account-count snapshot series (no currency). All pivoting is
// client-side (fetch-all); the pure core (src/lib/businessRevenueSeries.ts) owns
// the continuous-domain fill for both.

const REVENUE_KEY = ['xero', 'revenue'] as const;
const COUNT_HISTORY_KEY = ['account-analytics', 'count-history'] as const;

// Revenue dimensions (all 7, incl. Service area + Account).
const REVENUE_DIMENSIONS: { value: BusinessSeriesDimension; label: string }[] = [
  { value: 'agency', label: 'Agency' },
  { value: 'brand', label: 'Brand' },
  { value: 'industry', label: 'Industry' },
  { value: 'group', label: 'Group' },
  { value: 'am', label: 'Account manager' },
  { value: 'account', label: 'Account' },
  { value: 'code', label: 'Service area' },
];

// Count dimensions — a per-account or per-code "count" is nonsensical, so
// Service area + Account are hidden under the Account-counts metric.
const COUNT_DIMENSIONS: { value: CountSeriesDimension; label: string }[] = [
  { value: 'agency', label: 'Agency' },
  { value: 'brand', label: 'Brand' },
  { value: 'industry', label: 'Industry' },
  { value: 'group', label: 'Group' },
  { value: 'am', label: 'Account manager' },
];

const METRICS: { value: BusinessLensMetric; label: string }[] = [
  { value: 'accountIncome', label: 'Account income' },
  { value: 'recoupedMedia', label: 'Recouped media spend' },
  { value: 'accountCounts', label: 'Account counts' },
];

const COUNT_DIMENSION_VALUES = new Set<string>(COUNT_DIMENSIONS.map((d) => d.value));
const toDollars = (cents: number) => cents / 100;

export function BusinessTimeSeriesPanel({
  dimRows,
  accountIds,
}: {
  // Draft-applied roster rows — the source of brand/group/industry/AM dimensions.
  dimRows: AccountAnalyticsRow[];
  // The currently-filtered account set (revenue is scoped to it).
  accountIds: Set<string>;
}) {
  const [preset, setPreset] = useState<BusinessLensDatePreset>(DEFAULT_BUSINESS_LENS_PRESET);
  const [dimension, setDimension] = useState<BusinessSeriesDimension>('brand');
  const [metric, setMetric] = useState<BusinessLensMetric>('accountIncome');
  const [style, setStyle] = useState<ChartStyle>(DEFAULT_CHART_STYLE);
  const [currency, setCurrency] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const isCounts = metric === 'accountCounts';

  const revenue = useQuery({ queryKey: REVENUE_KEY, queryFn: getXeroRevenue });
  const rows = revenue.data?.rows ?? [];
  // Count history is fetched only when the counts metric is active.
  const countHistory = useQuery({
    queryKey: COUNT_HISTORY_KEY,
    queryFn: getAccountCountHistory,
    enabled: isCounts,
  });
  const history = countHistory.data ?? null;

  // Xero account code → display name (Service-area series labels).
  const codeNames = useMemo(() => {
    const src = revenue.data?.codeNames ?? {};
    return new Map<string, string>(Object.entries(src));
  }, [revenue.data?.codeNames]);

  const currencies = useMemo(() => seriesCurrencies(rows), [rows]);
  const activeCurrency = currency ?? currencies[0] ?? null;

  // "All Time" runs from the earliest data month → now (revenue: in the active
  // currency; counts: the earliest snapshot month).
  const earliest = useMemo(
    () =>
      isCounts
        ? history
          ? earliestCountMonth(history)
          : null
        : earliestRevenueMonth(rows, activeCurrency ?? undefined),
    [isCounts, history, rows, activeCurrency],
  );
  const range = useMemo(() => businessLensRange(preset, new Date(), earliest), [preset, earliest]);

  const dimByAccountId = useMemo(() => {
    const map = new Map<string, RevenueDimension>();
    for (const r of dimRows) {
      map.set(r.id, {
        accountName: r.name,
        brandLabel: r.brandName ?? '(no brand)',
        groupLabel: r.group ?? '(no group)',
        industryLabel: humanizeIndustry(r.industry),
        amLabel: r.accountManagerName ?? 'Unassigned',
      });
    }
    return map;
  }, [dimRows]);

  // The revenue result (money) — null under the counts metric / before data.
  const revenueResult = useMemo(
    () =>
      !isCounts && activeCurrency
        ? pivotRevenueSeries(rows, dimByAccountId, {
            dimension,
            metric,
            range,
            currency: activeCurrency,
            accountIds,
            codeNames,
          })
        : null,
    [isCounts, rows, dimByAccountId, dimension, metric, range, activeCurrency, accountIds, codeNames],
  );

  // The counts result (integers) — null unless the counts metric + history loaded.
  const countResult = useMemo(
    () =>
      isCounts && history
        ? pivotCountSeries(history, { dimension: dimension as CountSeriesDimension, range })
        : null,
    [isCounts, history, dimension, range],
  );

  // What the chart renders (both satisfy the shared base shape).
  const chartData: BusinessSeriesBase | null = isCounts ? countResult : revenueResult;

  const toggleSeries = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const changeMetric = (next: BusinessLensMetric) => {
    setMetric(next);
    setHidden(new Set());
    // Counts can't slice by Service area / Account — snap to a valid dimension.
    if (next === 'accountCounts' && !COUNT_DIMENSION_VALUES.has(dimension)) setDimension('brand');
  };

  // ---- loading / error / empty ----
  if (isCounts) {
    if (countHistory.isLoading) return <CenteredSpinner />;
    if (countHistory.error) {
      return <p className="text-sm text-red-600">Couldn't load account-count history. Try refreshing.</p>;
    }
  } else {
    if (revenue.isLoading) return <CenteredSpinner />;
    if (revenue.error) {
      return <p className="text-sm text-red-600">Couldn't load Xero revenue. Try refreshing.</p>;
    }
    if (rows.length === 0 || !activeCurrency) {
      return (
        <div className="space-y-4">
          <MetricSwitcher metric={metric} onChange={changeMetric} />
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center">
            <p className="text-sm font-medium text-slate-600">No invoiced revenue yet.</p>
            <p className="mt-0.5 text-sm text-slate-500">
              Connect Xero and map contacts on the{' '}
              <Link to="/settings/xero" className="font-medium text-kyck-700 underline">
                Xero connections
              </Link>{' '}
              page, then run a sync.
            </p>
          </div>
        </div>
      );
    }
  }

  const dimensions = isCounts ? COUNT_DIMENSIONS : REVENUE_DIMENSIONS;
  const metricLabel = METRICS.find((m) => m.value === metric)?.label ?? '';
  const dimensionLabel = dimensions.find((d) => d.value === dimension)?.label.toLowerCase() ?? '';
  const latestPoint = history?.points[history.points.length - 1] ?? null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-900">
          {isCounts ? 'Active accounts over time' : 'Invoiced revenue over time'}
        </h2>
        {/* Currency toggle — money only (counts aren't per-currency). */}
        {!isCounts && currencies.length > 1 && (
          <div className="flex gap-1">
            {currencies.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  setCurrency(c);
                  setHidden(new Set());
                }}
                aria-pressed={c === activeCurrency}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-semibold',
                  c === activeCurrency ? 'bg-kyck-50 text-kyck-700' : 'text-slate-500 hover:bg-slate-100',
                )}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Date-range presets (monthly). */}
      <div className="flex flex-wrap items-center gap-2">
        {BUSINESS_LENS_PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setPreset(p.value)}
            aria-pressed={preset === p.value}
            className={cn(
              'rounded-full px-3 py-1 text-sm transition-colors',
              preset === p.value
                ? 'bg-kyck-600 text-white'
                : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-50',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Range-scoped stat cards. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {isCounts ? (
          <>
            <RevenueStat
              label="Active operational accounts"
              value={latestPoint ? String(latestPoint.totalActiveOperational) : '—'}
              primary
            />
            <RevenueStat label="Brands" value={latestPoint ? String(Object.keys(latestPoint.byBrand).length) : '—'} />
            <RevenueStat label="Groups" value={latestPoint ? String(Object.keys(latestPoint.byGroup).length) : '—'} />
            <RevenueStat
              label="Account managers"
              value={
                latestPoint
                  ? String(Object.keys(latestPoint.byAm).filter((k) => k !== NO_AM_KEY).length)
                  : '—'
              }
            />
          </>
        ) : revenueResult ? (
          <>
            <RevenueStat
              label="Account income"
              value={formatReportingAmount(toDollars(revenueResult.summary.totalIncomeCents), activeCurrency!)}
              primary
            />
            <RevenueStat
              label="Recouped media (excl.)"
              value={formatReportingAmount(toDollars(revenueResult.summary.totalRecoupedCents), activeCurrency!)}
            />
            <RevenueStat label="Accounts with revenue" value={String(revenueResult.summary.accountCount)} />
            <RevenueStat label="Months with revenue" value={String(revenueResult.summary.monthCount)} />
          </>
        ) : null}
      </div>

      {/* Metric switcher (what value) + dimension switcher (how to split) +
          chart-style toggle. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <MetricSwitcher metric={metric} onChange={changeMetric} />
          <div className="inline-flex flex-wrap overflow-hidden rounded-md border border-slate-300 text-xs" role="group" aria-label="Series dimension">
            {dimensions.map((d, i) => {
              const active = d.value === dimension;
              return (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => {
                    setDimension(d.value);
                    setHidden(new Set());
                  }}
                  aria-pressed={active}
                  className={cn(
                    'px-2.5 py-1 font-medium transition-colors',
                    i > 0 && 'border-l border-slate-300',
                    active ? 'bg-kyck-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
        <ChartStyleToggle value={style} onChange={setStyle} />
      </div>

      {/* The star: the historic monthly time-series. */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-medium text-slate-700">
          {metricLabel} by month
          <span className="ml-2 text-xs font-normal text-slate-400">by {dimensionLabel}</span>
        </h3>
        {!chartData || chartData.series.length === 0 ? (
          <p className="flex h-64 items-center justify-center text-sm text-slate-400">
            {isCounts
              ? 'No account-count history in this range. History builds from the first monthly snapshot.'
              : 'No invoiced revenue in this range for the selected accounts.'}
          </p>
        ) : (
          <BusinessTimeSeriesChart
            result={chartData}
            style={style}
            currency={isCounts ? null : activeCurrency}
            hidden={hidden}
            onToggleSeries={toggleSeries}
          />
        )}
      </div>
    </div>
  );
}

function MetricSwitcher({
  metric,
  onChange,
}: {
  metric: BusinessLensMetric;
  onChange: (m: BusinessLensMetric) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs" role="group" aria-label="Metric">
      {METRICS.map((m, i) => {
        const active = m.value === metric;
        return (
          <button
            key={m.value}
            type="button"
            onClick={() => onChange(m.value)}
            aria-pressed={active}
            className={cn(
              'px-2.5 py-1 font-medium transition-colors',
              i > 0 && 'border-l border-slate-300',
              active ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
            )}
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

function RevenueStat({
  label,
  value,
  primary,
}: {
  label: string;
  value: string;
  primary?: boolean;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className={cn('text-xl font-semibold', primary ? 'text-kyck-700' : 'text-slate-900')}>{value}</div>
      <div className="mt-0.5 text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}
